import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    UserCheck, Calendar, Clock, MapPin, Phone, Mail, Home, Building2,
    AlertCircle, CheckCircle, XCircle, Filter, Search, RefreshCw,
    ChevronDown, Save, X, User, FileText, Zap, IndianRupee
} from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const AdminExpertVisits = () => {
    const { token } = useAuth();
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, pending: 0, scheduled: 0, completed: 0, cancelled: 0 });
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedVisit, setSelectedVisit] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [updating, setUpdating] = useState(false);

    const [updateForm, setUpdateForm] = useState({
        status: '',
        assigned_expert: '',
        scheduled_date: '',
        scheduled_time: '',
        admin_notes: '',
        assigned_salesman_id: '',
        assigned_salesman_name: ''
    });
    const [salesmen, setSalesmen] = useState([]);

    useEffect(() => {
        fetchVisits();
        fetchStats();
        fetchSalesmen();
    }, [statusFilter]);

    const fetchSalesmen = async () => {
        try {
            const res = await axios.get(`${API}/api/admin/salesmen`, { headers: { Authorization: `Bearer ${token}` } });
            setSalesmen(res.data.filter(s => s.status === 'active'));
        } catch (e) { setSalesmen([]); }
    };

    const fetchVisits = async () => {
        setLoading(true);
        try {
            const url = statusFilter === 'all'
                ? `${API}/api/admin/expert-visits`
                : `${API}/api/admin/expert-visits?status=${statusFilter}`;
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setVisits(response.data);
        } catch (error) {
            console.error('Failed to fetch visits:', error);
            // Demo data
            setVisits([
                {
                    id: 'ev-001',
                    name: 'Rajesh Kumar',
                    phone: '9876543210',
                    email: 'rajesh@email.com',
                    address: '123, Green Park, Sector 15',
                    city: 'Delhi',
                    pincode: '110001',
                    property_type: 'home',
                    roof_area_sqft: 500,
                    monthly_bill: 5000,
                    preferred_date: '2026-01-25',
                    preferred_time: 'morning',
                    notes: 'Looking for 5kW system with battery backup',
                    product_name: '5kW Complete Home Solar System',
                    status: 'pending',
                    created_at: '2026-01-22T10:30:00Z'
                },
                {
                    id: 'ev-002',
                    name: 'Priya Sharma',
                    phone: '8765432109',
                    email: 'priya@business.com',
                    address: 'Industrial Area B, Plot 42',
                    city: 'Mumbai',
                    pincode: '400001',
                    property_type: 'commercial',
                    roof_area_sqft: 2000,
                    monthly_bill: 25000,
                    preferred_date: '2026-01-26',
                    preferred_time: 'afternoon',
                    status: 'scheduled',
                    assigned_expert: 'Amit Verma',
                    scheduled_date: '2026-01-26',
                    scheduled_time: 'afternoon',
                    created_at: '2026-01-20T14:00:00Z'
                }
            ]);
        }
        setLoading(false);
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API}/api/admin/expert-visits/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(response.data);
        } catch (error) {
            console.log('Using demo stats');
            setStats({ total: 24, pending: 8, scheduled: 12, completed: 3, cancelled: 1 });
        }
    };

    const openUpdateModal = (visit) => {
        setSelectedVisit(visit);
        setUpdateForm({
            status: visit.status,
            assigned_expert: visit.assigned_expert || '',
            scheduled_date: visit.scheduled_date || visit.preferred_date || '',
            scheduled_time: visit.scheduled_time || visit.preferred_time || '',
            admin_notes: visit.admin_notes || '',
            assigned_salesman_id: visit.assigned_salesman_id || '',
            assigned_salesman_name: visit.assigned_salesman_name || ''
        });
        setShowModal(true);
    };

    const handleUpdate = async () => {
        if (!selectedVisit) return;

        setUpdating(true);
        try {
            await axios.put(
                `${API}/api/admin/expert-visits/${selectedVisit.id}`,
                updateForm,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Visit request updated successfully!');
            setShowModal(false);
            fetchVisits();
            fetchStats();
        } catch (error) {
            toast.error('Failed to update visit request');
        }
        setUpdating(false);
    };

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
            scheduled: 'bg-blue-100 text-blue-800 border-blue-300',
            completed: 'bg-green-100 text-green-800 border-green-300',
            cancelled: 'bg-red-100 text-red-800 border-red-300'
        };
        return styles[status] || styles.pending;
    };

    const getStatusIcon = (status) => {
        const icons = {
            pending: <AlertCircle className="w-4 h-4" />,
            scheduled: <Calendar className="w-4 h-4" />,
            completed: <CheckCircle className="w-4 h-4" />,
            cancelled: <XCircle className="w-4 h-4" />
        };
        return icons[status] || icons.pending;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const filteredVisits = visits.filter(v =>
        v.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.phone?.includes(searchQuery) ||
        v.city?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <DashboardLayout userRole="admin">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <UserCheck className="w-7 h-7 text-green-600" />
                            Expert Visit Requests
                        </h1>
                        <p className="text-muted-foreground">Manage and schedule solar expert home visits</p>
                    </div>
                    <Button onClick={() => { fetchVisits(); fetchStats(); }} variant="outline">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </Button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {[
                        { label: 'Total', value: stats.total, color: 'text-foreground', bg: 'bg-secondary' },
                        { label: 'Pending', value: stats.pending, color: 'text-yellow-600', bg: 'bg-yellow-50' },
                        { label: 'Scheduled', value: stats.scheduled, color: 'text-blue-600', bg: 'bg-blue-50' },
                        { label: 'Completed', value: stats.completed, color: 'text-green-600', bg: 'bg-green-50' },
                        { label: 'Cancelled', value: stats.cancelled, color: 'text-red-600', bg: 'bg-red-50' }
                    ].map((stat, i) => (
                        <div key={i} className={`${stat.bg} rounded-xl p-4 text-center`}>
                            <p className={`text-3xl font-bold ${stat.color}`}>{stat.value}</p>
                            <p className="text-sm text-muted-foreground">{stat.label}</p>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Search by name, phone, or city..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-full sm:w-48">
                            <Filter className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Filter by status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Requests</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="scheduled">Scheduled</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Visits List */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                        <p className="text-muted-foreground">Loading requests...</p>
                    </div>
                ) : filteredVisits.length === 0 ? (
                    <div className="text-center py-12 bg-card rounded-xl border">
                        <UserCheck className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="font-semibold">No Visit Requests Found</h3>
                        <p className="text-muted-foreground">
                            {searchQuery ? 'Try a different search term' : 'When customers request expert visits, they will appear here'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredVisits.map((visit, index) => (
                            <motion.div
                                key={visit.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="bg-card rounded-xl border p-5 hover:border-primary/50 transition-colors"
                            >
                                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                                    {/* Customer Info */}
                                    <div className="flex-1 space-y-3">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <h3 className="font-semibold text-lg flex items-center gap-2">
                                                    <User className="w-4 h-4 text-primary" />
                                                    {visit.name}
                                                </h3>
                                                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-1">
                                                    <span className="flex items-center gap-1">
                                                        <Phone className="w-3 h-3" /> {visit.phone}
                                                    </span>
                                                    {visit.email && (
                                                        <span className="flex items-center gap-1">
                                                            <Mail className="w-3 h-3" /> {visit.email}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <Badge className={`${getStatusBadge(visit.status)} flex items-center gap-1`}>
                                                {getStatusIcon(visit.status)}
                                                {visit.status}
                                            </Badge>
                                        </div>

                                        <div className="flex flex-wrap gap-4 text-sm">
                                            <span className="flex items-start gap-1">
                                                <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                                                <span>{visit.address}, {visit.city} - {visit.pincode}</span>
                                            </span>
                                            <span className="flex items-center gap-1">
                                                {visit.property_type === 'home' ? (
                                                    <Home className="w-4 h-4 text-muted-foreground" />
                                                ) : (
                                                    <Building2 className="w-4 h-4 text-muted-foreground" />
                                                )}
                                                {visit.property_type}
                                            </span>
                                            {visit.roof_area_sqft && (
                                                <span className="flex items-center gap-1">
                                                    <FileText className="w-4 h-4 text-muted-foreground" />
                                                    {visit.roof_area_sqft} sq ft
                                                </span>
                                            )}
                                            {visit.monthly_bill && (
                                                <span className="flex items-center gap-1">
                                                    <Zap className="w-4 h-4 text-muted-foreground" />
                                                    ₹{visit.monthly_bill.toLocaleString()}/month
                                                </span>
                                            )}
                                        </div>

                                        {visit.product_name && (
                                            <p className="text-sm">
                                                <span className="text-muted-foreground">Interested in:</span>{' '}
                                                <span className="font-medium text-primary">{visit.product_name}</span>
                                            </p>
                                        )}

                                        {visit.notes && (
                                            <p className="text-sm text-muted-foreground bg-muted/50 p-2 rounded">
                                                "{visit.notes}"
                                            </p>
                                        )}
                                    </div>

                                    {/* Schedule Info & Actions */}
                                    <div className="lg:w-64 space-y-3 lg:border-l lg:pl-4">
                                        <div className="text-sm space-y-1">
                                            <p className="text-muted-foreground">Preferred Schedule:</p>
                                            <p className="font-medium">
                                                {formatDate(visit.preferred_date)} • {visit.preferred_time || 'Any time'}
                                            </p>
                                        </div>

                                        {visit.scheduled_date && (
                                            <div className="text-sm p-2 bg-blue-50 dark:bg-blue-900/20 rounded space-y-1">
                                                <p className="text-blue-600 dark:text-blue-400 font-medium">Scheduled Visit:</p>
                                                <p>{formatDate(visit.scheduled_date)} • {visit.scheduled_time}</p>
                                                {visit.assigned_expert && (
                                                    <p className="text-xs">Expert: {visit.assigned_expert}</p>
                                                )}
                                                {visit.assigned_salesman_name && (
                                                    <p className="text-xs flex items-center gap-1">
                                                        <span className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 rounded px-1.5 py-0.5 font-medium">
                                                            🧑‍💼 {visit.assigned_salesman_name}
                                                        </span>
                                                        <span className={`ml-1 ${visit.salesman_accepted === true ? 'text-green-600' : visit.salesman_accepted === false ? 'text-red-600' : 'text-yellow-600'}`}>
                                                            {visit.salesman_accepted === true ? '✓ Accepted' : visit.salesman_accepted === false ? '✗ Rejected' : '⏳ Awaiting'}
                                                        </span>
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        <div className="text-xs text-muted-foreground">
                                            Requested: {formatDate(visit.created_at)}
                                        </div>

                                        <Button
                                            onClick={() => openUpdateModal(visit)}
                                            className="w-full"
                                            variant={visit.status === 'pending' ? 'default' : 'outline'}
                                        >
                                            {visit.status === 'pending' ? 'Schedule Visit' : 'Update Status'}
                                        </Button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>

            {/* Update Modal */}
            <AnimatePresence>
                {showModal && selectedVisit && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                        onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-card rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden"
                        >
                            <div className="bg-green-600 p-4 text-white flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Calendar className="w-6 h-6" />
                                    <div>
                                        <h3 className="font-semibold">Update Visit Request</h3>
                                        <p className="text-sm text-green-100">{selectedVisit.name}</p>
                                    </div>
                                </div>
                                <button onClick={() => setShowModal(false)} className="p-1 hover:bg-white/20 rounded">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                                <div className="space-y-2">
                                    <Label>Status</Label>
                                    <Select value={updateForm.status} onValueChange={(v) => setUpdateForm({ ...updateForm, status: v })}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="pending">Pending</SelectItem>
                                            <SelectItem value="scheduled">Scheduled</SelectItem>
                                            <SelectItem value="completed">Completed</SelectItem>
                                            <SelectItem value="cancelled">Cancelled</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>Assigned Expert</Label>
                                    <Input
                                        placeholder="Expert name..."
                                        value={updateForm.assigned_expert}
                                        onChange={(e) => setUpdateForm({ ...updateForm, assigned_expert: e.target.value })}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Scheduled Date</Label>
                                        <Input
                                            type="date"
                                            value={updateForm.scheduled_date}
                                            onChange={(e) => setUpdateForm({ ...updateForm, scheduled_date: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Scheduled Time</Label>
                                        <Select value={updateForm.scheduled_time} onValueChange={(v) => setUpdateForm({ ...updateForm, scheduled_time: v })}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select time" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="morning">Morning (9 AM - 12 PM)</SelectItem>
                                                <SelectItem value="afternoon">Afternoon (12 PM - 4 PM)</SelectItem>
                                                <SelectItem value="evening">Evening (4 PM - 7 PM)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Assign to Field Salesman</Label>
                                    <select
                                        className="w-full border rounded-lg px-3 py-2 bg-background text-sm"
                                        value={updateForm.assigned_salesman_id}
                                        onChange={(e) => {
                                            const s = salesmen.find(sm => sm.id === e.target.value);
                                            setUpdateForm({
                                                ...updateForm,
                                                assigned_salesman_id: e.target.value,
                                                assigned_salesman_name: s ? s.name : ''
                                            });
                                        }}
                                    >
                                        <option value="">— Do not assign salesman —</option>
                                        {salesmen.map(s => (
                                            <option key={s.id} value={s.id}>{s.name} {s.zone ? `(${s.zone})` : ''}</option>
                                        ))}
                                    </select>
                                    {updateForm.assigned_salesman_id && (
                                        <p className="text-xs text-muted-foreground">The selected salesman will receive this assignment in their dashboard</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label>Admin Notes</Label>
                                    <Textarea
                                        placeholder="Internal notes about this visit..."
                                        value={updateForm.admin_notes}
                                        onChange={(e) => setUpdateForm({ ...updateForm, admin_notes: e.target.value })}
                                        rows={3}
                                    />
                                </div>
                            </div>

                            <div className="p-4 border-t bg-muted/30 flex justify-end gap-3">
                                <Button variant="outline" onClick={() => setShowModal(false)}>
                                    Cancel
                                </Button>
                                <Button onClick={handleUpdate} disabled={updating} className="bg-green-600 hover:bg-green-700">
                                    <Save className="w-4 h-4 mr-2" />
                                    {updating ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </DashboardLayout>
    );
};

export default AdminExpertVisits;
